import React, { useEffect, useState } from "react";
import closeIcon from "../../../../../../Assets/images/close-icon.svg";
import redIcon from "../../../../../../Assets/images/close-red.svg";
import calendarIcon from "../../../../../../Assets/images/calender.svg";
import Loader from "../../../../../../Context/Loader/Loader";
import apiClient from "../../../../../../Config/apiClient";
import "./AddTruckModal.scss"

const truckPath = process.env.REACT_APP_TC_TRUCK_BASE_PATH;

const AddEditTruckModal = ({ show, onClose, onSave, truckData, newTruckId }) => {
    const [formData, setFormData] = useState({
        truck_id: "",
        vehicle_no: "",
        permit_no: "",
        license_plate_no: "",
        insurance_no: "",
        truck_type: "Tri-Axle Dump Truck",
        cvor_expiry: "",
        permit_expiry: "",
        insurance_expiry: "",
        emission_expiry: "",
        documents: {
            cvor_doc: null,
            permit_doc: null,
            insurance_doc: null,
            emission_doc: null,
        },
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetchNextTruckId = async () => {
            if (!truckData) {
                try {
                    const res = await apiClient.get(`${truckPath}/next-id`);
                    if (res.data.status === "SUCCESS") {
                        setFormData((prev) => ({ ...prev, truck_id: res.data.data.nextId }));
                    }
                } catch (err) {
                    console.error("Failed to fetch next truck ID", err);
                }
            }
        };
        fetchNextTruckId();
    }, [truckData]);

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    useEffect(() => {
        if (truckData) {
            setFormData({
                truck_id: truckData.truck_id || "",
                vehicle_no: truckData.vehicle_no || "",
                permit_no: truckData.permit_no || "",
                license_plate_no: truckData.license_plate_no || "",
                insurance_no: truckData.insurance_no || "",
                truck_type: "Tri-Axle Dump Truck",
                cvor_expiry: truckData.cvor_expiry ? formatDate(truckData.cvor_expiry) : "",
                permit_expiry: truckData.permit_expiry ? formatDate(truckData.permit_expiry) : "",
                insurance_expiry: truckData.insurance_expiry ? formatDate(truckData.insurance_expiry) : "",
                emission_expiry: truckData.emission_expiry ? formatDate(truckData.emission_expiry) : "",
                documents: {
                    cvor_doc: null,
                    permit_doc: null,
                    insurance_doc: null,
                    emission_doc: null,
                }
            });
        } else {
            setFormData((prev) => ({
                ...prev,
                truck_id: newTruckId || "",
                vehicle_no: "",
                permit_no: "",
                license_plate_no: "",
                insurance_no: "",
                truck_type: "Tri-Axle Dump Truck",
                documents: {
                    cvor_doc: null,
                    permit_doc: null,
                    insurance_doc: null,
                    emission_doc: null,
                },
            }));
        }
        setErrors({});
    }, [truckData, newTruckId]);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (files && files[0]) {
            const file = files[0];
            if (file.type !== "application/pdf") {
                setErrors((prev) => ({ ...prev, [name]: "Only PDF files allowed" }));
                return;
            }
            setFormData((prev) => ({
                ...prev,
                documents: { ...prev.documents, [name]: file },
            }));
            setErrors((prev) => ({ ...prev, [name]: "" }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
            setErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const handleRemoveFile = (field) => {
        setFormData((prev) => ({
            ...prev,
            documents: { ...prev.documents, [field]: null },
        }));
        setErrors((prev) => ({ ...prev, [field]: "" }));

        const input = document.getElementById(field);
        if (input) input.value = "";
    };

    const validateForm = () => {
        const newErrors = {};
        const today = new Date();

        const expiryFieldLabels = {
            cvor_expiry: "CVOR expiry date",
            permit_expiry: "Permit expiry date",
            insurance_expiry: "Insurance expiry date",
            emission_expiry: "Emission expiry date",
        };

        Object.keys(expiryFieldLabels).forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = `${expiryFieldLabels[field]} is required`;
            } else {
                const selectedDate = new Date(formData[field]);
                if (selectedDate <= today) {
                    newErrors[field] = `${expiryFieldLabels[field]} must be greater than today`;
                }
            }
        });

        if (!formData.vehicle_no) newErrors.vehicle_no = "Vehicle number is required";
        if (!formData.permit_no) newErrors.permit_no = "Permit number is required";
        if (!formData.license_plate_no) newErrors.license_plate_no = "License plate number is required";
        if (!formData.insurance_no) newErrors.insurance_no = "Insurance number is required";

        const docFields = ["cvor_doc", "permit_doc", "insurance_doc", "emission_doc"];
        const docLabels = {
            cvor_doc: "CVOR document",
            permit_doc: "Permit document",
            insurance_doc: "Insurance document",
            emission_doc: "Emission document",
        };

        docFields.forEach((field) => {
            const uploadedFile = formData.documents[field];
            const dbValue = truckData?.[field];
            if (!uploadedFile && !dbValue) {
                newErrors[field] = `${docLabels[field]} is required`;
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        try {
            setSubmitting(true);
            await onSave(formData);
        } finally {
            setSubmitting(false);
        }
    };

    if (!show) return null;

    return (
        <div className="modal right rtm-modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.15)" }}>
            <div className="modal-dialog rtl-dialog">
                <div className="modal-content rtl-content animate__animated animate__fadeInRight rtl-overflow">
                    <div className="ajob-content">
                        <div className="cpf-top cpf-border">
                            <h2>{truckData ? "Edit Truck" : "Add Truck"}</h2>
                            <button className="m-close" onClick={onClose}>
                                <img src={closeIcon} alt="Close" />
                            </button>
                        </div>

                        <div className="modal-inner addtruck-form">
                            <div className="adddriver-form scroll-padding">
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="cp-split">
                                            <label className="fi-label driver-lbl">ID <span style={{ color: "red" }}>*</span></label>
                                            <input type="text" className="common-input truckid-disable" value={truckData ? `T-${formData.truck_id}` : "AUTO GENERATED"} disabled />
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className={`cp-split ${errors.vehicle_no ? "error" : ""}`}>
                                            <label className="fi-label driver-lbl">Vehicle Number <span style={{ color: "red" }}>*</span></label>
                                            <input type="text" className="common-input" name="vehicle_no" value={formData.vehicle_no} onChange={handleChange} />
                                            {errors.vehicle_no && <span className="error-text active">{errors.vehicle_no}</span>}
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className={`cp-split ${errors.permit_no ? "error" : ""}`}>
                                            <label className="fi-label driver-lbl">Permit (MTO Permit) <span style={{ color: "red" }}>*</span></label>
                                            <input type="text" className="common-input" name="permit_no" value={formData.permit_no} onChange={handleChange} />
                                            {errors.permit_no && <span className="error-text active">{errors.permit_no}</span>}
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className={`cp-split ${errors.license_plate_no ? "error" : ""}`}>
                                            <label className="fi-label driver-lbl">License Plate Number <span style={{ color: "red" }}>*</span></label>
                                            <input type="text" className="common-input" name="license_plate_no" value={formData.license_plate_no} onChange={handleChange} />
                                            {errors.license_plate_no && <span className="error-text active">{errors.license_plate_no}</span>}
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className={`cp-split ${errors.insurance_no ? "error" : ""}`}>
                                            <label className="fi-label driver-lbl">Insurance Number <span style={{ color: "red" }}>*</span></label>
                                            <input type="text" className="common-input" name="insurance_no" value={formData.insurance_no} onChange={handleChange} />
                                            {errors.insurance_no && <span className="error-text active">{errors.insurance_no}</span>}
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="cp-split">
                                            <label className="fi-label driver-lbl">Truck Type <span style={{ color: "red" }}>*</span></label>
                                            <input type="text" className="common-input" value="Tri-Axle Dump Truck" disabled />
                                        </div>
                                    </div>
                                </div>
                                <div className="doc-cover">
                                    <h2 className="sub-head">Documents</h2>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="cp-split">
                                                <label className="fi-label driver-lbl">CVOR <span style={{ color: "red" }}>*</span></label>
                                                <div className={`hsfile-upload ${errors.cvor_doc ? "error-border" : ""}`}>
                                                    <div className="doc-display">
                                                        <label className="file-upload-label">
                                                            {formData.documents.cvor_doc?.name || formData.documents.cvor_doc || ""}
                                                        </label>
                                                        {formData.documents.cvor_doc && (
                                                            <img src={redIcon} alt="Remove" className="remove-icon" onClick={() => handleRemoveFile("cvor_doc")} />
                                                        )}
                                                    </div>
                                                    <label htmlFor="cvor" className="browse-btn">Browse</label>
                                                    <input key={formData.documents.cvor_doc ? formData.documents.cvor_doc.name : "cvor"} id="cvor" name="cvor_doc" type="file" accept=".pdf" onChange={handleChange} hidden />
                                                </div>
                                                {errors.cvor_doc && <span className="error-text active">CVOR Document is required</span>}
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="cp-split">
                                                <label className="fi-label driver-lbl">
                                                    CVOR Expiry <span style={{ color: "red" }}>*</span>
                                                </label>
                                                <div className="custom-datetime-wrapper">
                                                    <input type="date" name="cvor_expiry" id="cvor-expiry-datetime" className={`custom-datetime-input common-input ${errors.cvor_expiry ? "error" : ""}`} value={formData.cvor_expiry} onChange={handleChange} />
                                                    <img src={calendarIcon} alt="Calendar" className="calendar-icon" onClick={() => document.getElementById("cvor-expiry-datetime")?.showPicker()} style={{ cursor: "pointer" }} />
                                                </div>
                                                {errors.cvor_expiry && (
                                                    <span className="error-text active">{errors.cvor_expiry}</span>
                                                )}
                                            </div>
                                        </div>


                                        <div className="col-md-6">
                                            <div className="cp-split">
                                                <label className="fi-label driver-lbl">Permit Document <span style={{ color: "red" }}>*</span></label>
                                                <div className={`hsfile-upload ${errors.permit_doc ? "error-border" : ""}`}>
                                                    <div className="doc-display">
                                                        <label className="file-upload-label">
                                                            {formData.documents.permit_doc?.name || formData.documents.permit_doc || ""}
                                                        </label>
                                                        {formData.documents.permit_doc && (
                                                            <img src={redIcon} alt="Remove" className="remove-icon" onClick={() => handleRemoveFile("permit_doc")} />
                                                        )}
                                                    </div>
                                                    <label htmlFor="permit_doc" className="browse-btn">Browse</label>
                                                    <input key={formData.documents.permit_doc ? formData.documents.permit_doc.name : "permit_doc"} id="permit_doc" name="permit_doc" type="file" accept=".pdf" onChange={handleChange} hidden />
                                                </div>
                                                {errors.permit_doc && <span className="error-text active">Permit Document is required</span>}
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="cp-split">
                                                <label className="fi-label driver-lbl">Permit Expiry <span style={{ color: "red" }}>*</span></label>
                                                <div className="custom-datetime-wrapper">
                                                    <input type="date" name="permit_expiry" id="permit-expiry-datetime" className={`custom-datetime-input common-input ${errors.permit_expiry ? "error" : ""}`} value={formData.permit_expiry} onChange={handleChange} />
                                                    <img src={calendarIcon} alt="Calendar" className="calendar-icon" onClick={() => document.getElementById("permit-expiry-datetime")?.showPicker()} style={{ cursor: "pointer" }} />
                                                </div>
                                                {errors.cvor_expiry && (
                                                    <span className="error-text active">{errors.permit_expiry}</span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="cp-split">
                                                <label className="fi-label driver-lbl">Insurance <span style={{ color: "red" }}>*</span></label>
                                                <div className={`hsfile-upload ${errors.insurance_doc ? "error-border" : ""}`}>
                                                    <div className="doc-display">
                                                        <label className="file-upload-label">
                                                            {formData.documents.insurance_doc?.name || formData.documents.insurance_doc || ""}
                                                        </label>
                                                        {formData.documents.insurance_doc && (
                                                            <img src={redIcon} alt="Remove" className="remove-icon" onClick={() => handleRemoveFile("insurance_doc")} />
                                                        )}
                                                    </div>
                                                    <label htmlFor="insurance" className="browse-btn">Browse</label>
                                                    <input key={formData.documents.insurance_doc ? formData.documents.insurance_doc.name : "insurance"} id="insurance" name="insurance_doc" type="file" accept=".pdf" onChange={handleChange} hidden />
                                                </div>
                                                {errors.insurance_doc && <span className="error-text active">Insurance Document is required</span>}
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="cp-split">
                                                <label className="fi-label driver-lbl">Insurance Expiry <span style={{ color: "red" }}>*</span></label>
                                                <div className="custom-datetime-wrapper">
                                                    <input type="date" name="insurance_expiry" id="insurance-expiry-datetime" className={`custom-datetime-input common-input ${errors.insurance_expiry ? "error" : ""}`} value={formData.insurance_expiry} onChange={handleChange} />
                                                    <img src={calendarIcon} alt="Calendar" className="calendar-icon" onClick={() => document.getElementById("insurance-expiry-datetime")?.showPicker()} style={{ cursor: "pointer" }} />
                                                </div>
                                                {errors.cvor_expiry && (
                                                    <span className="error-text active">{errors.insurance_expiry}</span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="cp-split">
                                                <label className="fi-label driver-lbl">Emission <span style={{ color: "red" }}>*</span></label>
                                                <div className={`hsfile-upload ${errors.emission_doc ? "error-border" : ""}`}>
                                                    <div className="doc-display">
                                                        <label className="file-upload-label">
                                                            {formData.documents.emission_doc?.name || formData.documents.emission_doc || ""}
                                                        </label>
                                                        {formData.documents.emission_doc && (
                                                            <img src={redIcon} alt="Remove" className="remove-icon" onClick={() => handleRemoveFile("emission_doc")} />
                                                        )}
                                                    </div>
                                                    <label htmlFor="emission" className="browse-btn">Browse</label>
                                                    <input key={formData.documents.emission_doc ? formData.documents.emission_doc.name : "emission"} id="emission" name="emission_doc" type="file" accept=".pdf" onChange={handleChange} hidden />
                                                </div>
                                                {errors.emission_doc && <span className="error-text active">Emission Document is required</span>}
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="cp-split">
                                                <label className="fi-label driver-lbl">Emission Expiry <span style={{ color: "red" }}>*</span></label>
                                                <div className="custom-datetime-wrapper">
                                                    <input type="date" name="emission_expiry" id="emission-expiry-datetime" className={`custom-datetime-input common-input ${errors.emission_expiry ? "error" : ""}`} value={formData.emission_expiry} onChange={handleChange} />
                                                    <img src={calendarIcon} alt="Calendar" className="calendar-icon" onClick={() => document.getElementById("emission-expiry-datetime")?.showPicker()} style={{ cursor: "pointer" }} />
                                                </div>
                                                {errors.cvor_expiry && (
                                                    <span className="error-text active">{errors.emission_expiry}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="m-button">
                                    <button className="cp-button save-btn" onClick={handleSubmit} disabled={submitting} >
                                        {submitting ? <Loader /> : truckData ? "Update" : "Save"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddEditTruckModal;